/*
 * @Author: Ryan
 * @Date: 2026-01-16
 * @Description: Landing 页面布局组件 - 包含 Header、Footer、SEOHead
 */
import type { NextPage } from 'next';
import type { ReactElement, ReactNode } from 'react';
import React from 'react';

import Footer from '@/components/Footer';

import LandingHeader from '../LandingHeader';
import SEOHead, { SEOHeadProps } from '../SEOHead';
import styles from './index.module.scss';

export interface LandingLayoutProps {
  /** SEO 配置（可选，不传则不渲染 SEOHead） */
  seo?: SEOHeadProps;
  /** 头部是否透明 */
  transparentHeader?: boolean;
  /** 子内容 */
  children: React.ReactNode;
}

const LandingLayout: React.FC<LandingLayoutProps> = ({
  seo,
  transparentHeader = false,
  children,
}) => {
  return (
    <>
      {seo && <SEOHead {...seo} />}
      <div className={styles.layout}>
        <LandingHeader transparent={transparentHeader} />
        <main className={styles.main}>{children}</main>
        <Footer />
      </div>
    </>
  );
};

/**
 * 高阶函数，为页面组件添加 LandingLayout
 * 使用 getLayout 模式，确保 Layout 在路由切换时不会重新挂载
 * 同时标记 isLandingPage = true，让 _app.tsx 跳过 Uploader 和神策 SDK 初始化
 */
export function withLandingLayout<P = object>(
  page: NextPage<P>,
  options?: { transparentHeader?: boolean },
): NextPage<P> {
  const PageWithLayout = page as NextPage<P> & {
    getLayout?: (page: ReactElement) => ReactNode;
    isLandingPage?: boolean;
  };

  PageWithLayout.getLayout = (pageElement: ReactElement) => {
    return (
      <div className={styles.layout}>
        <LandingHeader transparent={options?.transparentHeader} />
        <main className={styles.main}>{pageElement}</main>
        <Footer />
      </div>
    );
  };

  // 标记为 Landing 页面，_app.tsx 会跳过重型 SDK 初始化
  PageWithLayout.isLandingPage = true;

  return PageWithLayout;
}

export default LandingLayout;
